import { useEffect, useMemo, useState } from 'react';

import { getTheme } from '@dynatrace-sdk/user-preferences';

import { isBrowser } from '../utils/isBrowser.js';

/**
 * Returns the current theme based on sdk-setting or browsers preferred color-scheme.
 * @public
 */
export function useCurrentTheme(): 'light' | 'dark' {
  const sdkTheme = useMemo(() => {
    return getTheme();
  }, []);

  if (sdkTheme === 'dark' || sdkTheme === 'light') {
    return sdkTheme;
  }

  if (!isBrowser) {
    return 'light';
  }

  // suppressing the rules of hooks because the sdkTheme is a constant that cannot
  // change through the applications' lifecycle.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const themeQuery = useMemo(
    () => window && window.matchMedia('(prefers-color-scheme: dark)'),
    [],
  );

  // suppressing the rules of hooks because the sdkTheme is a constant that cannot
  // change through the applications' lifecycle.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>(
    themeQuery.matches ? 'dark' : 'light',
  );

  // suppressing the rules of hooks because the sdkTheme is a constant that cannot
  // change through the applications' lifecycle.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const handlePrefersColorSchemeChange = () => {
      const prefersDark = themeQuery.matches;
      setCurrentTheme(prefersDark ? 'dark' : 'light');
    };

    themeQuery.addEventListener('change', handlePrefersColorSchemeChange);
    return () => {
      themeQuery.removeEventListener('change', handlePrefersColorSchemeChange);
    };
  }, [themeQuery]);

  return currentTheme;
}
